<template>
  <article
    class="ai-msg ai-rise"
    :class="[`ai-msg--${message.role}`, { 'is-streaming': message.streaming, 'is-error': !!message.error }]"
    :data-testid="`ai-message-${message.role}`"
  >
    <span v-if="message.role === 'assistant'" aria-hidden="true" class="ai-mark ai-msg__avatar">KI</span>

    <div class="ai-msg__bubble">
      <!-- Nutzertext: reiner Text, Zeilenumbrüche bleiben erhalten. -->
      <p v-if="message.role === 'user'" class="ai-msg__user-text">{{ message.content }}</p>

      <template v-else>
        <div v-if="message.error" class="ai-msg__error">
          <p class="ai-msg__error-text">
            <AlertTriangle aria-hidden="true" :size="14" />
            <span>{{ message.error }}</span>
          </p>
          <button class="ai-text-btn ai-msg__error-report" type="button" @click="$emit('report')">
            <Bug aria-hidden="true" :size="14" />
            {{ $gt('Fehler melden') }}
          </button>
        </div>

        <div v-else class="ai-md">
          <template v-for="(block, b) in blocks" :key="b">
            <h4 v-if="block.kind === 'heading'" class="ai-md__h">
              <InlineRuns :runs="block.runs" @navigate="navigate" />
            </h4>
            <p v-else-if="block.kind === 'paragraph'" class="ai-md__p">
              <InlineRuns :runs="block.runs" @navigate="navigate" />
            </p>
            <component :is="block.ordered ? 'ol' : 'ul'" v-else class="ai-md__list" :class="{ 'is-ordered': block.ordered }">
              <li v-for="(item, i) in block.items" :key="i">
                <InlineRuns :runs="item" @navigate="navigate" />
              </li>
            </component>
          </template>
          <span v-if="message.streaming" aria-hidden="true" class="ai-md__caret" />
        </div>

        <footer v-if="!message.streaming && !message.error" class="ai-msg__footer">
          <div v-if="message.sources.length > 0" class="ai-msg__sources">
            <BookOpen aria-hidden="true" :size="12" />
            <button
              v-for="source in message.sources.slice(0, 3)"
              :key="source.id"
              class="ai-msg__source"
              :title="$gt('Im Hilfe-Center öffnen')"
              type="button"
              @click="openSource(source)"
            >
              {{ source.title }}
            </button>
          </div>
          <div v-if="message.id" class="ai-msg__rating">
            <button
              :aria-label="$gt('Hilfreich')"
              :aria-pressed="message.rating === 1"
              class="ai-icon-btn ai-icon-btn--dark ai-msg__rate"
              :class="{ 'is-on': message.rating === 1 }"
              type="button"
              @click="$emit('rate', message, 1)"
            >
              <ThumbsUp :size="14" />
            </button>
            <button
              :aria-label="$gt('Nicht hilfreich')"
              :aria-pressed="message.rating === -1"
              class="ai-icon-btn ai-icon-btn--dark ai-msg__rate"
              :class="{ 'is-on is-down': message.rating === -1 }"
              type="button"
              @click="$emit('rate', message, -1)"
            >
              <ThumbsDown :size="14" />
            </button>
          </div>
        </footer>
      </template>
    </div>
  </article>
</template>

<script setup lang="ts">
  import type { AssistantSource } from '@/services/assistantApi'
  import type { AssistantMessage } from '@/stores/assistantStore'
  import { AlertTriangle, ArrowRight, BookOpen, Bug, ThumbsDown, ThumbsUp } from 'lucide-vue-next'
  import { computed, defineComponent, h, type PropType } from 'vue'
  import { type RouteLocationRaw, useRouter } from 'vue-router'
  import { resolveRouteToken } from './assistantLinks'
  import { type InlineRun, parseBlocks, streamingSafeText } from './assistantMarkdown'
  import { useTypewriter } from './useTypewriter'

  const props = defineProps<{ message: AssistantMessage }>()

  const emit = defineEmits<{
    (e: 'rate', message: AssistantMessage, rating: 1 | -1): void
    (e: 'navigate', to: RouteLocationRaw): void
    (e: 'report'): void
  }>()

  const router = useRouter()
  const catalog = {
    hasRoute: (name: string) => router.hasRoute(name),
    pathOf: (name: string) => router.getRoutes().find(r => r.name === name)?.path,
  }

  // computed statt toRef: Der Store ersetzt das Nachrichtenobjekt bei jedem

  // Delta (immutables Patch); toRef bliebe am ersten Objekt hängen und der

  // Schreibmaschinen-Effekt sähe nie neuen Text.

  const content = computed(() => props.message.content)
  const streaming = computed(() => props.message.streaming)
  const { displayed } = useTypewriter(content, streaming)

  const blocks = computed(() => parseBlocks(props.message.streaming ? streamingSafeText(displayed.value) : props.message.content))

  function navigate (to: RouteLocationRaw) {
    emit('navigate', to)
  }

  function openSource (source: AssistantSource) {
    const to: RouteLocationRaw = source.route
      ? { name: source.route.name, query: source.route.query, params: source.route.params }
      : { name: 'PageHelp', query: { artikel: source.id } }
    emit('navigate', to)
  }

  /**
   * Rendert Inline-Läufe als Text, <strong>, <code> oder Link-Pille. Als
   * Render-Funktion, damit ausschließlich Textknoten entstehen (kein HTML
   * aus Modellantworten).
   */
  const InlineRuns = defineComponent({
    name: 'AssistantInlineRuns',
    props: { runs: { type: Array as PropType<InlineRun[]>, required: true } },
    emits: ['navigate'],
    setup (p, { emit: emitRun }) {
      return () => p.runs.map((run, i) => {
        switch (run.kind) {
          case 'bold': {
            return h('strong', { key: i }, run.text)
          }
          case 'code': {
            return h('code', { key: i, class: 'ai-md__code' }, run.text)
          }
          case 'route': {
            const to = resolveRouteToken(catalog, run.target)
            if (!to) {
              return h('span', { key: i }, run.label)
            }
            return h('button', {
              'key': i,
              'type': 'button',
              'class': 'ai-link',
              'data-testid': 'ai-route-link',
              'onClick': () => emitRun('navigate', to),
            }, [run.label, h(ArrowRight, { 'size': 13, 'aria-hidden': 'true' })])
          }
          default: {
            return run.text
          }
        }
      })
    },
  })
</script>

<style scoped>
  .ai-msg {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    max-width: 100%;
  }

  .ai-msg--user {
    justify-content: flex-end;
  }

  .ai-msg__avatar {
    flex: none;
    width: 26px;
    height: 26px;
    margin-bottom: 2px;
  }

  .ai-msg__bubble {
    min-width: 0;
    max-width: 88%;
    padding: 10px 14px;
    border-radius: 12px;
    color: var(--ai-ink);
    font-size: 14px;
    line-height: 1.5;
  }

  .ai-msg--assistant .ai-msg__bubble {
    border: 1px solid var(--ai-line);
    border-bottom-left-radius: 4px;
    background: var(--ai-surface);
  }

  .ai-msg--user .ai-msg__bubble {
    border-bottom-right-radius: 4px;
    background: var(--ai-blue);
    color: #fff;
  }

  .ai-msg__user-text {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .ai-msg__error {
    display: grid;
    gap: 8px;
  }

  .ai-msg__error-text {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin: 0;
    color: #b42318;
  }

  .ai-msg__error-report {
    justify-self: start;
    color: var(--ai-blue);
  }

  .ai-md__h {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 700;
  }

  .ai-md__p {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .ai-md__p + .ai-md__p,
  .ai-md__list + .ai-md__p,
  .ai-md__p + .ai-md__list,
  .ai-md__list + .ai-md__list,
  .ai-md__p + .ai-md__h,
  .ai-md__list + .ai-md__h {
    margin-top: 10px;
  }

  .ai-md__list {
    display: grid;
    gap: 6px;
    margin: 0;
    padding-left: 20px;
  }

  .ai-md__list.is-ordered li::marker {
    color: var(--ai-blue);
    font-weight: 700;
  }

  .ai-md :deep(.ai-md__code) {
    padding: 1px 6px;
    border-radius: 4px;
    background: var(--ai-surface-3);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
  }

  .ai-md__caret {
    display: inline-block;
    width: 2px;
    height: 1em;
    margin-left: 2px;
    vertical-align: -2px;
    background: var(--ai-blue);
    animation: ai-blink 1s steps(1) infinite;
  }

  .ai-msg__footer {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid var(--ai-line);
  }

  .ai-msg__sources {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    min-width: 0;
    color: var(--ai-faint);
    font-size: 12px;
  }

  .ai-msg__source {
    max-width: 180px;
    overflow: hidden;
    padding: 2px 8px;
    border: 1px solid var(--ai-line);
    border-radius: 999px;
    background: var(--ai-surface-2);
    color: var(--ai-muted);
    font: inherit;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    transition: color 120ms ease, border-color 120ms ease;
  }

  .ai-msg__source:hover {
    border-color: var(--ai-blue);
    color: var(--ai-blue);
  }

  .ai-msg__rating {
    display: flex;
    flex: none;
    gap: 2px;
  }

  .ai-msg__rate {
    width: 28px;
    height: 28px;
    color: var(--ai-faint);
  }

  .ai-msg__rate.is-on {
    background: var(--ai-success-soft);
    color: #15803d;
  }

  .ai-msg__rate.is-down {
    background: var(--ai-danger-soft);
    color: #b42318;
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-md__caret {
      animation: none;
    }
  }
</style>
